import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import { keyframes } from "@mui/system";

const ACCENT = "#0061FE";

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

/** Shimmering placeholder with a small spinner, laid over a cover while it loads. */
export function CoverLoader({ dark = false }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
        bgcolor: dark ? "rgba(255,255,255,0.04)" : "#EEF2F7",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: dark
            ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)"
            : "linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent)",
          animation: `${shimmer} 1.4s ease-in-out infinite`,
        },
      }}
    >
      <CircularProgress size={22} thickness={4.5} sx={{ color: ACCENT, opacity: 0.85, position: "relative" }} />
    </Box>
  );
}

CoverLoader.propTypes = { dark: PropTypes.bool };

const COVER_PALETTES = [
  ["#E0E7FF", "#C7D2FE", "#4F46E5"],
  ["#FCE7F3", "#FBCFE8", "#DB2777"],
  ["#DCFCE7", "#BBF7D0", "#059669"],
  ["#E0F2FE", "#BAE6FD", "#0284C7"],
  ["#FEF3C7", "#FDE68A", "#D97706"],
  ["#EDE9FE", "#DDD6FE", "#7C3AED"],
];

function paletteFor(text) {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  return COVER_PALETTES[Math.abs(hash) % COVER_PALETTES.length];
}

/** Designed stand-in cover for slides with no loadable image. */
export function GeneratedCover({ title = "", dark = false, label = null, icon = "biotech" }) {
  const [from, to, ink] = paletteFor(title || "slide");
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        gap: 0.5,
        p: 1.5,
        lineHeight: 1.3,
        background: dark
          ? `linear-gradient(140deg, ${ink}55 0%, rgba(15,23,42,0.95) 100%)`
          : `linear-gradient(140deg, ${from} 0%, ${to} 100%)`,
      }}
    >
      <Icon
        aria-hidden
        sx={{
          position: "absolute",
          right: -14,
          top: -14,
          fontSize: "112px !important",
          color: dark ? "rgba(255,255,255,0.08)" : `${ink}22`,
          transform: "rotate(-12deg)",
        }}
      >
        {icon}
      </Icon>
      {label ? (
        <Box
          component="span"
          sx={{
            alignSelf: "flex-start",
            px: 0.9,
            py: 0.25,
            borderRadius: 999,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: dark ? "#fff" : ink,
            bgcolor: dark ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.7)",
          }}
        >
          {label}
        </Box>
      ) : null}
      <Box
        component="span"
        sx={{
          position: "relative",
          fontSize: 14,
          fontWeight: 800,
          color: dark ? "#fff" : ink,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {title}
      </Box>
    </Box>
  );
}

GeneratedCover.propTypes = {
  title: PropTypes.string,
  dark: PropTypes.bool,
  label: PropTypes.string,
  icon: PropTypes.string,
};

/**
 * Cover <img> that shows the loader until decoded, then fades in.
 * If the image fails, `fallback` (when given) is rendered instead.
 */
export function LoadingImage({ src, alt = "", dark = false, objectPosition = "center", fallback = null }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  if (failed && fallback) return fallback;

  return (
    <>
      {!loaded ? <CoverLoader dark={dark} /> : null}
      <Box
        component="img"
        src={src}
        alt={alt}
        draggable={false}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setFailed(true);
          setLoaded(true);
        }}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition,
          display: "block",
          margin: 0,
          padding: 0,
          opacity: loaded && !failed ? 1 : 0,
          transition: "opacity 280ms ease",
        }}
      />
    </>
  );
}

LoadingImage.propTypes = {
  fallback: PropTypes.node,
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  dark: PropTypes.bool,
  objectPosition: PropTypes.string,
};

/** iframe (live 3D preview) with the loader shown until its document loads. */
export function LoadingIframe({ src, title, dark = false }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(false), [src]);

  return (
    <>
      {!loaded ? <CoverLoader dark={dark} /> : null}
      <iframe
        src={src}
        title={title}
        onLoad={() => setLoaded(true)}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
          opacity: loaded ? 1 : 0,
          transition: "opacity 280ms ease",
        }}
      />
    </>
  );
}

LoadingIframe.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  dark: PropTypes.bool,
};
